import { NormalizedVoucher, PersonBenefits, Promotion, normalizeVouchers } from '@esportal/portal-service';

import React from 'react';
import styled from 'styled-components';
import {
	Breakpoints,
	ContainerPadding,
	Container,
	TokenTextAppearance,
	TokenTextColor,
	Text,
	ContainerGutter,
	TextAlignment,
	Layout,
} from '@volkswagen-onehub/components-core';
import { PortalContext } from '../../context/portal-context';
import { vwBenefitsRedirect } from '../../helpers/benefits-helper';
import { HuchaContainer } from './hucha-bonos/hucha-container';
import { BenefitsCard } from './vouchers-promotions/benefits-card';
import { BenefitsCarousel } from './vouchers-promotions/benefits-carousel';
import { useBenefitsPopupLayer } from './vouchers-promotions/popup/use-benefits-popup-layer';
import { useBenefitsTracker } from './vouchers-promotions/hooks/use-benefits-tracker';
import { useNavigate } from 'react-router-dom';

export type BenefitsCombined =
	| { type: 'promotion'; priority: number; data: Promotion }
	| { type: 'voucher'; priority: number; data: NormalizedVoucher };

const buildBenefitItems = (personBenefits: PersonBenefits): BenefitsCombined[] => {
	const promotionItems: BenefitsCombined[] = (personBenefits.benefits.promotions ?? [])
		.filter((promotion) => promotion.creativity !== null)
		.map((promotion) => ({
			type: 'promotion',
			priority: promotion.priority ?? 0,
			data: promotion,
		}));

	const voucherItems: BenefitsCombined[] = normalizeVouchers(personBenefits).map((voucher) => ({
		type: 'voucher',
		priority: voucher.priority,
		data: voucher,
	}));

	return [...promotionItems, ...voucherItems].sort((first, second) => second.priority - first.priority);
};

const BenefitsContainer = styled(Container)`
	background-color: #ebe5dc;
`;

const PrimaryBenefitsGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 50px;

	@media (min-width: 960px) {
		grid-template-columns: repeat(2, 1fr);
	}

	& > *:only-child {
		@media (min-width: 960px) {
			grid-column: 1 / -1;
			justify-self: center;
		}
	}
`;

export const Benefits: React.FC<{
	personBenefits: PersonBenefits;
}> = ({ personBenefits }) => {
	const { portalService } = React.useContext(PortalContext);
	const { navigationService, userInteractionService } = React.useContext(PortalContext);
	const userData = portalService?.getPerson()?.getValue();

	const benefitsCombined = React.useMemo(() => buildBenefitItems(personBenefits), [personBenefits]);
	const { trackBenefitClick, trackBenefitOpenLayerClick } = useBenefitsTracker();

	const { handleLayerOpen } = useBenefitsPopupLayer();

	const navigate = useNavigate();

	const handleClickBenefit = (benefit: BenefitsCombined) => {
		if (benefit.type === 'promotion') {
			const ctaSrc = benefit.data.creativity?.card.cta?.src;

			console.log(ctaSrc);

			if (ctaSrc) {
				trackBenefitClick(benefit);
				vwBenefitsRedirect(ctaSrc, navigationService, userInteractionService);
			} else {
				trackBenefitOpenLayerClick(benefit);
				handleLayerOpen(benefit);
			}
			return;
		}

		let voucherBenefit: NormalizedVoucher = benefit.data;

		if (voucherBenefit.source === 'vwData') {
			if (voucherBenefit.layer) {
				trackBenefitOpenLayerClick(benefit);
				handleLayerOpen(benefit);
			} else {
				trackBenefitClick(benefit);
				vwBenefitsRedirect(voucherBenefit.card.cta?.src, navigationService, userInteractionService);
			}
		} else if (voucherBenefit.source === 'salesforce') {
			trackBenefitClick(benefit);
			navigate(`/voucher/salesforce/${voucherBenefit.id}`);
			window.scrollTo(0, 0);
		} else {
			console.error('voucher source error');
		}
	};

	const primaryBenefits = benefitsCombined.slice(0, 2);
	const carouselBenefits = benefitsCombined.slice(2);

	return (
		<BenefitsContainer
			padding={{
				top: ContainerPadding.dynamic0130,
				bottom: ContainerPadding.dynamic0130,
			}}
			horizontalAlign={'center'}
		>
			{benefitsCombined.length > 0 && (
				<Container
					padding={{
						bottom: ContainerPadding.dynamic0150,
					}}
					gutter={ContainerGutter.dynamic0150}
					wrap={'always'}
					stretchContent
				>
					{primaryBenefits.length > 0 && (
						<Container
							padding={{
								left: ContainerPadding.grid001,
								right: ContainerPadding.grid001,
							}}
							gutter={ContainerGutter.dynamic0100}
							wrap="always"
						>
							<Container data-testid="benefits-section-title" wrap="always" gutter={ContainerGutter.dynamic0020}>
								<Text
									appearance={TokenTextAppearance.bigcopy200}
									color={TokenTextColor.primary}
									textAlign={TextAlignment.left}
									bold
								>
									Beneficios
								</Text>
								<Text
									appearance={TokenTextAppearance.headline500}
									color={TokenTextColor.primary}
									textAlign={TextAlignment.left}
								>
									Para ti, por <b>ser del club</b>
								</Text>
							</Container>

							<PrimaryBenefitsGrid data-testid="primary-benefits-layout">
								{primaryBenefits.map((benefit, index) => (
									<BenefitsCard
										key={`${benefit.type}-${index}`}
										benefit={benefit}
										handleClickBenefit={handleClickBenefit}
										primary
									/>
								))}
							</PrimaryBenefitsGrid>
						</Container>
					)}

					{carouselBenefits.length > 0 && (
						<Container wrap="always">
							<Layout
								data-testid="benefits-layout"
								appearance={{
									[Breakpoints.default]: [{ name: 'benefits-section', columns: 24 }],
								}}
							>
								<BenefitsCarousel benefitsCombined={carouselBenefits} handleClickBenefit={handleClickBenefit} />
							</Layout>
						</Container>
					)}
				</Container>
			)}
			<Layout
				data-testid="hucha-layout"
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 1 },
						{ name: 'hucha-section', columns: 22 },
						{ name: '.', columns: 1 },
					],
					[Breakpoints.b1920]: [
						{ name: '.', columns: 3 },
						{ name: 'hucha-section', columns: 18 },
						{ name: '.', columns: 3 },
					],
				}}
			>
				<Container data-testid="hucha-container" gutter={ContainerGutter.dynamic0100} wrap="always">
					<Text
						appearance={TokenTextAppearance.headline400}
						color={TokenTextColor.primary}
						textAlign={TextAlignment.left}
					>
						<b>Tu fidelidad</b> tiene premio
					</Text>
					<HuchaContainer userData={userData} personBenefits={personBenefits} />
				</Container>
			</Layout>
		</BenefitsContainer>
	);
};
